import React, { FC, memo, PropsWithChildren, useContext } from 'react';

import { ChunkedImageProperties } from '../types/chunkedImage';
import { LengthRange } from '../types/lengthRange';

export interface ChunkLoaderContextProps {
	/**
	 * Look up a cached chunk by its image identity. The provider owns key construction so callers
	 * stay ignorant of the cache namespace (e.g. drillhole). Returns undefined if no chunk has been
	 * fetched for this identity yet.
	 */
	getChunk: (
		imageTypeId: number,
		range: LengthRange,
		resolution: number,
		isUncropped: boolean,
	) => ChunkedImageProperties | undefined;
	queueChunk: (range: LengthRange, resolution: number, imageTypeId: number, isUncropped: boolean) => string;
	cancelChunk: (handle: string) => boolean;
}

const ChunkLoaderContext = React.createContext<ChunkLoaderContextProps>({
	getChunk: () => undefined,
	queueChunk: () => '',
	cancelChunk: () => true,
});
export { ChunkLoaderContext };

const ChunkLoaderProviderInternal: FC<PropsWithChildren<{ value: ChunkLoaderContextProps }>> = ({
	value,
	children,
}) => <ChunkLoaderContext.Provider value={value}>{children}</ChunkLoaderContext.Provider>;

// eslint-disable-next-line react-refresh/only-export-components
export const useChunkLoaderContext = (): ChunkLoaderContextProps => {
	return useContext(ChunkLoaderContext);
};

export const ChunkLoaderProvider = memo(ChunkLoaderProviderInternal);
